import {
  hasPermissions,
  getUserPermissions,
  getCurrentAccount,
} from './permissionsHelper';

import {
  ROLES,
  CONVERSATION_PERMISSIONS,
  CONTACT_PERMISSIONS,
  REPORTS_PERMISSIONS,
  PORTAL_PERMISSIONS,
} from 'dashboard/constants/permissions.js';

export const routeIsAccessibleFor = (route, userPermissions = []) => {
  const { meta: { permissions: routePermissions = [] } = {} } = route;
  return hasPermissions(routePermissions, userPermissions);
};

export const defaultRedirectPage = (to, permissions) => {
  const { accountId } = to.params;

  // SECURITY: Agents should be redirected to conversations, not dashboard
  // Only administrators can access the analytics dashboard
  const isAdmin = permissions.includes('administrator');

  // If user is admin, they can go to dashboard
  // If user is agent (or other role), redirect to conversations
  if (isAdmin) {
    return `accounts/${accountId}/dashboard`;
  }

  // For agents and custom roles, redirect to conversations by default
  const permissionRoutes = [
    {
      permissions: [...ROLES, ...CONVERSATION_PERMISSIONS],
      path: 'conversations',
    },
    { permissions: [CONTACT_PERMISSIONS], path: 'contacts' },
    { permissions: [REPORTS_PERMISSIONS], path: 'reports/overview' },
    { permissions: [PORTAL_PERMISSIONS], path: 'portals' },
  ];

  const route = permissionRoutes.find(({ permissions: routePermissions }) =>
    hasPermissions(routePermissions, permissions)
  );

  return `accounts/${accountId}/${route ? route.path : 'conversations'}`;
};

const validateActiveAccountRoutes = (to, user) => {
  const userPermissions = getUserPermissions(user, to.params.accountId);
  const isAdmin = userPermissions.includes('administrator');

  // SECURITY: Redirect to appropriate page based on role
  // Admins go to dashboard, agents go to conversations
  const defaultURL = isAdmin
    ? `accounts/${to.params.accountId}/dashboard`
    : `accounts/${to.params.accountId}/conversations`;

  // If the user is trying to access suspended route, redirect them to appropriate page
  if (to.name === 'account_suspended') {
    return defaultURL;
  }

  const isAccessible = routeIsAccessibleFor(to, userPermissions);
  // If the route is not accessible for the user, return to appropriate screen
  return isAccessible ? null : defaultRedirectPage(to, userPermissions);
};

// Routes that should be accessible even when account is suspended
// This allows users to resolve payment issues
const SUSPENDED_ACCOUNT_ALLOWED_ROUTES = [
  'account_suspended',
  'billing_settings_index',
  'billing_settings_pricing',
];

export const validateLoggedInRoutes = (to, user) => {
  const currentAccount = getCurrentAccount(user, Number(to.params.accountId));
  // If current account is missing, either user does not have
  // access to the account or the account is deleted, return to login screen
  if (!currentAccount) {
    return `app/login`;
  }

  const isCurrentAccountActive = currentAccount.status === 'active';

  if (isCurrentAccountActive) {
    return validateActiveAccountRoutes(to, user);
  }

  // If the current account is not active, allow access to billing routes
  // so users can resolve payment issues
  if (SUSPENDED_ACCOUNT_ALLOWED_ROUTES.includes(to.name)) {
    return null;
  }

  // If the current account is not active, then redirect the user to the suspended screen
  return `accounts/${to.params.accountId}/suspended`;
};

export const isAConversationRoute = (
  routeName,
  includeBase = false,
  includeExtended = true
) => {
  const baseRoutes = [
    'home',
    'conversation_mentions',
    'conversation_unattended',
    'inbox_dashboard',
    'label_conversations',
    'team_conversations',
    'folder_conversations',
    'conversation_participating',
  ];
  const extendedRoutes = [
    'inbox_conversation',
    'conversation_through_mentions',
    'conversation_through_unattended',
    'conversation_through_inbox',
    'conversations_through_label',
    'conversations_through_team',
    'conversations_through_folders',
    'conversation_through_participating',
  ];

  const routes = [
    ...(includeBase ? baseRoutes : []),
    ...(includeExtended ? extendedRoutes : []),
  ];

  return routes.includes(routeName);
};

export const getConversationDashboardRoute = routeName => {
  switch (routeName) {
    case 'inbox_conversation':
      return 'home';
    case 'conversation_through_mentions':
      return 'conversation_mentions';
    case 'conversation_through_unattended':
      return 'conversation_unattended';
    case 'conversations_through_label':
      return 'label_conversations';
    case 'conversations_through_team':
      return 'team_conversations';
    case 'conversations_through_folders':
      return 'folder_conversations';
    case 'conversation_through_participating':
      return 'conversation_participating';
    case 'conversation_through_inbox':
      return 'inbox_dashboard';
    default:
      return null;
  }
};

export const isAInboxViewRoute = (routeName, includeBase = false) => {
  const baseRoutes = ['inbox_view'];
  const extendedRoutes = ['inbox_view_conversation'];
  const routeNames = includeBase
    ? [...baseRoutes, ...extendedRoutes]
    : extendedRoutes;
  return routeNames.includes(routeName);
};

export const isNotificationRoute = routeName =>
  routeName === 'notifications_index';
